<!-- 组件设计：CommonSearch查询处理handleQuery放在父组件页面；
  CommonPagination数据pageData:{pageNum,pageSize,totalCount}在页面内使用hooks统一生成，可供CommonSearch、CommonPagination修改使用；
  CommonTable只做展示，不操作其他组件数据 -->
<template>
  <div class="common-table-component">
    <!-- :max-height 不能随ref值修改而重新渲染，使用:style？？？ -->
    <el-table
      ref="tableRef"
      :data="tableData.tableList"
      :stripe="props.stripe"
      :max-height="maxHeight"
      :header-cell-style="props.headerCellStyle ? { background: '#EBF3FE', color: '#333' } : undefined"
      :show-summary="!!props.summaryMethod"
      :summary-method="!!props.summaryMethod ? props.summaryMethod : undefined"
      :highlight-current-row="props.hightlightCurrentRow"
      @select="handleSelect"
      @select-all="handleSelectAll"
      @selection-change="handleSelectionChange"
      @current-change="handleCurrentChange"
      @row-dblclick="handleDblClick"
      @row-click="handleRowClick"
      @row-contextmenu="handleRowContextmenu"
      @cell-click="handleCellClick"
      :row-class-name="props.tableRowClassName"
      :header-row-class-name="props.tableHeaderRowClassName"
      :cell-class-name="props.cellClassName"
      :header-cell-class-name="props.headerCellClassName"
      :span-method="props.spanMethod"
      :style="maxHeight ? { 'max-height': maxHeight + 'px' } : undefined"
      v-loading="props.loading"
      :empty-text="props.emptyText"
      :border="props.border"
      :default-sort="defaultSort"
      :row-key="props.rowKey"
      :default-expand-all="props.defaultExpandAll"
    >
      <el-table-column v-if="!!props.isShowIndex" type="index" width="55"></el-table-column>
      <el-table-column
        v-if="!!props.isShowSelection"
        :selectable="selectable"
        type="selection"
        width="55"
      ></el-table-column>
      <el-table-column
        v-for="(item, index) in columnList"
        :key="index"
        :prop="item.prop"
        :label="item.label"
        :min-width="item.minWidth || 110"
        :width="item.width || undefined"
        :sortable="item.sortable"
        :show-overflow-tooltip="item.showOverflowTooltip != null ? item.showOverflowTooltip : true"
        :fixed="'fixed' in item ? item.fixed : false"
      >
        <template v-if="item.headerSlotName" #header="scope">
          {{ item.label }} <slot v-if="item.headerSlotName" :name="item.headerSlotName" v-bind="scope"></slot>
        </template>

        <!-- 任意页面调用父组件<CommonTable> <template #name1="scope"></template> <template #name2="scope"></template> </CommonTable>，
          其内多个命名插槽（分属各个 <el-table-column> ） 占位借助各个 <el-table-column> 下的 <template #default> 默认插槽实现，再通过 <slot :name="item.slotName"> 名称唯一匹配。
          通过 <template  #default="scope"> 把当前子组件作用域传递给  <slot v-bind="scope"> 命名插槽，再传回给父组件内对应的<template #name="scope">，
          最终把子组件作用域变量传递给父组件  -->
        <template v-if="item.slotName" #default="scope">
          <slot v-if="item.slotName" :name="item.slotName" v-bind="scope"></slot>
        </template>

        <!-- 二级表头，每个表头下的多个 el-table-column -->
        <template v-if="item.children?.length">
          <el-table-column
            v-for="(subItem, subIndex) in item.children"
            :key="subIndex"
            :prop="subItem.prop"
            :label="subItem.label"
            :min-width="subItem.minWidth || 110"
            :width="subItem.width || undefined"
            :show-overflow-tooltip="subItem.showOverflowTooltip != null ? subItem.showOverflowTooltip : true"
          >
            <template v-if="subItem.headerSlotName" #header="scope">
              {{ subItem.label }}
              <slot v-if="subItem.headerSlotName" :name="subItem.headerSlotName" v-bind="scope"></slot>
            </template>
            <template v-if="subItem.slotName" #default="scope">
              <slot v-if="subItem.slotName" :name="subItem.slotName" v-bind="scope"></slot>
            </template>
          </el-table-column>
        </template>
      </el-table-column>
      <el-table-column
        v-if="props.isShowOption"
        :min-width="optionColumnWidth"
        :label="props.optionColumnName || '操作'"
        fixed="right"
      >
        <template #default="scope">
          <slot name="optionColumn" v-bind="scope"></slot>
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, reactive, ref, toRef, toRefs, inject, watch, Ref, nextTick } from 'vue'
import { useTableMaxHeight } from '@/hooks/index'
import { debounce } from 'lodash-es'
import { TableData, ColumnItem } from '@/types/index'

let props = withDefaults(
  defineProps<{
    tableData: TableData
    columnList: ColumnItem[]
    isShowIndex?: boolean //索引列
    isShowSelection?: boolean //多选框列
    isShowOption?: boolean //操作列
    summaryMethod?: (data: { columns: any[]; data: any[] }) => string[]
    loading?: boolean
    emptyText?: string
    optionColumnName?: string
    hightlightCurrentRow?: boolean //高亮样式 + 单选（重置复选框行为）
    border?: boolean
    headerCellStyle?: boolean //标题行默认背景色
    stripe?: boolean
    maxHeight?: number
    tableRowClassName?: (data: { row: any; rowIndex: number }) => string | string //行的 className 的回调方法
    tableHeaderRowClassName?: (data: { row: any; rowIndex: number }) => string | string //表头行的 className 的回调方法
    cellClassName?: (data: { row: any; column: any; rowIndex: number; columnIndex: number }) => string | string
    headerCellClassName?: (data: { row: any; column: any; rowIndex: number; columnIndex: number }) => string | string
    spanMethod?: any
    optionColumnWidth?: number
    selectable?: (row: any, index: number) => boolean // type=selection列，复选框是否可勾选
    defaultSort?: any
    rowKey?: string //树形数据 row-key
    defaultExpandAll?: boolean //树形数据 default-expand-all
    useMaxHeight?: boolean //表格是否显示滚动条
  }>(),
  {
    tableData: () => ({
      tableList: [],
      multipleSelection: [],
    }),
    isShowIndex: false,
    isShowSelection: false,
    isShowOption: false,
    loading: false,
    hightlightCurrentRow: false,
    border: false,
    headerCellStyle: true,
    stripe: true,
    optionColumnWidth: 130,
    useMaxHeight: true,
  },
)

const emit = defineEmits(['dblClick', 'currentChange', 'selectionChange', 'rowClick', 'rowContextmenu', 'cellClick'])

let { tableData, columnList } = toRefs(props)
let tableRef = ref()

defineExpose({
  tableRef,
})

// 勾选
const handleSelect = (selection: any[], row: any) => {
  // console.log('handleSelect', selection, row)
  // 单选时，自动取消之前的选择
  if (props.hightlightCurrentRow) {
    tableData.value.multipleSelection = [row]
    tableRef.value?.clearSelection()
    tableRef.value?.toggleRowSelection(row, true)
  }
}
// 全选
const handleSelectAll = (selection: any[]) => {
  // console.log('handleSelectAll', selection)
  // 单选时，禁止自带的全选功能
  if (props.hightlightCurrentRow) {
    tableRef.value?.clearSelection()
    if (tableData.value.multipleSelection?.length) {
      tableRef.value?.toggleRowSelection(tableData.value.multipleSelection[0], true)
    }
  }
}
// 多选-选项改变
const handleSelectionChange = (val: any[]) => {
  // console.log('selection-change', val)
  if (!props.hightlightCurrentRow) {
    tableData.value.multipleSelection = val
  }
  emit('selectionChange')
}
// 单选
const handleCurrentChange = (currentRow: any, oldCurrentRow: any) => {
  // console.log('handleCurrentChange--')
  // 单选时，自动更新复选框选中状态
  if (props.hightlightCurrentRow) {
    tableData.value.multipleSelection = [currentRow]
    tableRef.value?.clearSelection()
    tableRef.value?.toggleRowSelection(currentRow, true)
  }
  emit('currentChange')
}
// 单击行
const handleRowClick = (row, column, event) => {
  // console.log('handleRowClick--')
  // 单选时，自动更新复选框选中状态
  if (props.hightlightCurrentRow) {
    tableData.value.multipleSelection = [row]
    tableRef.value?.clearSelection()
    tableRef.value?.toggleRowSelection(row, true)
  }
  emit('rowClick', row)
}
// 双击行
const handleDblClick = (row: any) => {
  emit('dblClick', row)
}
// 右击行
const handleRowContextmenu = (row, column, event) => {
  event.preventDefault()
  emit('rowContextmenu', { row, column, event })
}
// 单击单元格
const handleCellClick = (row, column, cell, event) => {
  // console.log('handleCellClick--')
  emit('cellClick', { row, column, cell, event })
}

let maxHeight = ref(props.useMaxHeight ? props.maxHeight ?? 600 : undefined)

// 多层嵌套的 CommonTable 组件，在适当的时机重新获取 maxHeight，上传组件 provide 注入响应式变量
let tableIsShow: Ref<boolean> = inject('tableIsShow', ref(false))
watch(
  () => tableIsShow?.value,
  debounce(newValue => {
    if (newValue === true) {
      nextTick(() => {
        if (!props.maxHeight && props.useMaxHeight) {
          maxHeight.value = useTableMaxHeight()
        }
      })
    }
  }, 50),
)

onMounted(() => {
  if (!props.maxHeight && props.useMaxHeight) {
    maxHeight.value = useTableMaxHeight() //组件 mounted 但可能并未显示，基于 document.querySelector('.el-table') 计算的高度可能出错
    // console.log('mounted--', maxHeight.value)
    window.addEventListener(
      'resize',
      debounce(() => {
        maxHeight.value = useTableMaxHeight()
      }, 150),
    )
  }
})
</script>

<style scoped lang="less">
:deep .el-table__row.warning-row {
  background-color: rgb(253, 246, 236);
}
</style>
